import React, { useEffect, useState } from 'react';
import { Plus, Trash2, Pencil, X } from 'lucide-react';
import { api } from '../../lib/api';
import { useToast } from '../../hooks/use-toast';

const TAGS = ['Announcement', 'Progress', 'Community', 'Feature', 'Press'];
const empty = { title: '', date: '', tag: 'Announcement', excerpt: '', body: '', image_url: '', published: true };

const PostsManager = () => {
  const { toast } = useToast();
  const [posts, setPosts] = useState([]);
  const [form, setForm] = useState(empty);
  const [editing, setEditing] = useState(null);
  const [open, setOpen] = useState(false);

  const load = () => api.get('/posts', { params: { all: true } }).then((r) => setPosts(r.data || [])).catch(() => setPosts([]));
  useEffect(() => { load(); }, []);

  const upd = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const openNew = () => { setEditing(null); setForm(empty); setOpen(true); };
  const openEdit = (p) => { setEditing(p.id); setForm({ ...p }); setOpen(true); };

  const save = async (e) => {
    e.preventDefault();
    try {
      if (editing) await api.put(`/posts/${editing}`, form);
      else await api.post('/posts', form);
      toast({ title: editing ? 'Post updated' : 'Post published' });
      setOpen(false); load();
    } catch { toast({ title: 'Save failed', variant: 'destructive' }); }
  };

  const remove = async (id) => {
    if (!window.confirm('Delete this post?')) return;
    try { await api.delete(`/posts/${id}`); setPosts((x) => x.filter((p) => p.id !== id)); toast({ title: 'Deleted' }); }
    catch { toast({ title: 'Delete failed', variant: 'destructive' }); }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h3 className="font-display text-xl text-forest">News & Posts ({posts.length})</h3>
        <button onClick={openNew} className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-gold text-white font-600 text-sm hover:bg-gold-light"><Plus className="w-4 h-4" /> New Post</button>
      </div>

      <div className="space-y-3">
        {posts.map((p) => (
          <div key={p.id} className="flex items-center gap-4 bg-white rounded-xl border border-sand p-4">
            <div className="flex-1">
              <p className="font-600 text-forest">{p.title} {!p.published && <span className="text-xs text-ink/40">(draft)</span>}</p>
              <p className="text-xs text-ink/55">{p.date} · {p.tag}</p>
            </div>
            <button onClick={() => openEdit(p)} className="w-9 h-9 rounded-lg bg-sand text-forest hover:bg-forest hover:text-cream flex items-center justify-center"><Pencil className="w-4 h-4" /></button>
            <button onClick={() => remove(p.id)} className="w-9 h-9 rounded-lg bg-red-50 text-red-500 hover:bg-red-100 flex items-center justify-center"><Trash2 className="w-4 h-4" /></button>
          </div>
        ))}
        {posts.length === 0 && <p className="text-ink/50 text-sm">No posts yet. Create your first announcement.</p>}
      </div>

      {open && (
        <div className="fixed inset-0 z-[70] bg-forest-dark/60 flex items-center justify-center p-4" onClick={() => setOpen(false)}>
          <form onClick={(e) => e.stopPropagation()} onSubmit={save} className="bg-cream rounded-2xl p-7 w-full max-w-xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-5">
              <h3 className="font-display text-xl text-forest">{editing ? 'Edit Post' : 'New Post'}</h3>
              <button type="button" onClick={() => setOpen(false)}><X className="w-5 h-5 text-ink/50" /></button>
            </div>
            <div className="space-y-4">
              <input required value={form.title} onChange={upd('title')} placeholder="Headline" className="w-full px-3 py-2.5 rounded-lg border border-sand text-sm focus:border-gold focus:outline-none" />
              <div className="grid grid-cols-2 gap-4">
                <select value={form.tag} onChange={upd('tag')} className="px-3 py-2.5 rounded-lg border border-sand text-sm focus:border-gold focus:outline-none">{TAGS.map((t) => <option key={t}>{t}</option>)}</select>
                <input value={form.date} onChange={upd('date')} placeholder="Date (e.g. June 12, 2025)" className="px-3 py-2.5 rounded-lg border border-sand text-sm focus:border-gold focus:outline-none" />
              </div>
              <input value={form.image_url} onChange={upd('image_url')} placeholder="Image URL (paste a gallery or web image link)" className="w-full px-3 py-2.5 rounded-lg border border-sand text-sm focus:border-gold focus:outline-none" />
              <textarea rows={2} value={form.excerpt} onChange={upd('excerpt')} placeholder="Short excerpt" className="w-full px-3 py-2.5 rounded-lg border border-sand text-sm focus:border-gold focus:outline-none resize-none" />
              <textarea rows={6} value={form.body} onChange={upd('body')} placeholder="Full story / text content" className="w-full px-3 py-2.5 rounded-lg border border-sand text-sm focus:border-gold focus:outline-none resize-none" />
              <label className="flex items-center gap-2 text-sm text-ink/70"><input type="checkbox" checked={form.published} onChange={(e) => setForm({ ...form, published: e.target.checked })} /> Published (visible on site)</label>
            </div>
            <button type="submit" className="w-full mt-6 py-3 rounded-full bg-gold text-white font-600 hover:bg-gold-light">{editing ? 'Save Changes' : 'Publish Post'}</button>
          </form>
        </div>
      )}
    </div>
  );
};

export default PostsManager;
